/**
 * Stripe Checkout helper.
 *
 * Single source for "start a subscription purchase" UX across:
 *   • Chalkboard expired-banner Renew button
 *   • UpgradeGateCard "Upgrade" button
 *   • Settings/More billing section
 *   • Clipboard+ parent upgrade prompt
 *
 * Architecture rule: this NEVER activates a subscription. It only kicks off a
 * Stripe Checkout Session; the webhook is the sole activation source. After
 * the redirect, /billing/success polls /subscriptions/me to confirm activation.
 */
import { api } from "./chalkApi";
import { track } from "./analytics";
import { loadBillingStatus, EARLY_ACCESS_NOTE } from "./billingStatus";

/**
 * Start a Stripe Checkout flow.
 *
 * @param {object} opts
 * @param {"chalkboard"|"clipboard_plus"} opts.tier
 * @param {"monthly"|"annual"} [opts.interval]
 * @param {string} [opts.teamId]
 * @param {object} [opts.context] - { feature_name, source_screen, current_tier } for analytics
 * @returns {Promise<string>} session id (browser is redirected before this resolves on success)
 */
export async function startCheckout({ tier, interval = "monthly", teamId, context = {} } = {}) {
  const status = await loadBillingStatus();
  if (!status.payments_open) throw Object.assign(new Error(EARLY_ACCESS_NOTE), { code: "STRIPE_NOT_CONFIGURED" });
  const origin = window.location.origin;
  const payload = {
    tier,
    billing_interval: interval,
    success_url: `${origin}/billing/success`,
    cancel_url: `${origin}/billing/cancelled`,
  };
  if (teamId) payload.team_id = teamId;
  track("upgrade_checkout_started", {
    feature_name: context.feature_name || null,
    source_screen: context.source_screen || null,
    current_tier: context.current_tier || null,
    team_id: teamId || context.team_id || null,
    target_tier: tier,
  });
  const data = await api.createCheckout(payload);
  if (data && data.first_charge_at) {
    try { sessionStorage.setItem("chalk_first_charge_at", data.first_charge_at); } catch { /* ignore */ }
  }
  if (!data || !data.checkout_url) {
    throw new Error("Couldn't open checkout. Please try again.");
  }
  // Save the destination so /billing/success can bounce the user back where they were.
  try {
    sessionStorage.setItem("chalk_post_billing_return", window.location.pathname);
  } catch {
    /* ignore quota issues */
  }
  window.location.href = data.checkout_url;
  return data.session_id;
}
